import React, { Suspense, useEffect } from 'react';
import { connect, MapDispatchToProps } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { RouteComponentProps, withRouter } from 'src/hooks/routerCompat';
import { bindActionCreators } from 'redux';
import { ensureSelectedCompany } from '../../actions/companyActions';
import { ensureUserRoles } from '../../actions/userRoleActions';
import { Loadable } from '../../models/';
import { AccountDetail, PageParameters, PageStringParameters } from '../../models/account';
import { Company } from '../../models/company';
import { AppState } from '../../models/state/appState';
import LoadingIndicator from '../ui/LoadingIndicator';
import { UserSettings } from '../../models/state/userSettings';
import { getBaseUrl } from '../../services/helpers';
import { SimsPageSearchContextProvider } from '../SimsPageSearchContext';

const loadAccountRoutes = () => import('../account/AccountRoutes');
const loadSimsPage = () => import('../sim/simsPage/SimsPage');
const loadSimsActionPage = () => import('../sim/command/simActionPage/SimsActionPage');
const loadSpendCapsPage = () => import('../spendcaps/SpendCapsPage');
const loadSubmissionsPage = () => import('../sim/submission/SubmissionsPage');
const loadSubmissionPage = () => import('../sim/submission/SubmissionPage');
const loadSimOrders = () => import('../simorder/SimOrdersPage');
const loadSimOrderForm = () => import('../simorder/CreateSimOrder');
const loadGraphicalDashboard = () => import('../graphicalDashboard/GraphicalDashboard');
const loadAccountSelectionPage = () => import('../account/AccountSelectionPage');

const AccountRoutes = React.lazy(loadAccountRoutes);
const SimsPage = React.lazy(loadSimsPage);
const SimsActionPage = React.lazy(loadSimsActionPage);
const SpendCapsPage = React.lazy(loadSpendCapsPage);
const SubmissionsPage = React.lazy(loadSubmissionsPage);
const SubmissionPage = React.lazy(loadSubmissionPage);
const SimOrders = React.lazy(loadSimOrders);
const SimOrderForm = React.lazy(loadSimOrderForm);
const GraphicalDashboard = React.lazy(loadGraphicalDashboard);
const AccountSelectionPage = React.lazy(loadAccountSelectionPage);

interface DispatchToProps {
  actions: {
    ensureSelectedCompany(id: number): void;
    ensureUserRoles(value: PageParameters): void;
  };
}

interface StateToProps {
  company: Loadable<Company>;
  userSettings: UserSettings;
}

type OwnProps = PageParameters;
type CompanyRoutesComponentProps = RouteComponentProps<PageStringParameters>;
type CompanyRoutesProps = OwnProps & CompanyRoutesComponentProps & DispatchToProps & StateToProps;

function CompanyRoutes(props: CompanyRoutesProps) {
  useEffect(() => {
    props.actions.ensureSelectedCompany(props.companyId);
    ensureUserRoles();
  }, [props.companyId, props.accountId]);

  function ensureUserRoles() {
    props.actions.ensureUserRoles({
      companyId: props.companyId,
      accountId: props.accountId
    });
  }

  const company = props.company;
  if (company.isLoading) {
    return <LoadingIndicator label="Loading" size="small" textPosition="bottom" />;
  }

  if (company.id === 0 && company.error) {
    if (
      props.userSettings.companyId &&
      props.userSettings.companyId !== props.companyId &&
      props.userSettings.companyId !== 0
    ) {
      props.history.push(`${getBaseUrl(props.userSettings.companyId, props.userSettings.accountId)}sim`);
    }
    return (
      <div className="alert alert-danger">
        <h1>Company not found.</h1>
      </div>
    );
  }

  const companyUrl = '/company/:companyId/';
  const simsPageSearchScopeKey = `${props.companyId}:${props.accountId ?? 'all'}`;

  return (
    <div id="company-route" className="container-fluid layout h-100">
      {!company.isLoading && !company.error ? (
        <Suspense fallback={<LoadingIndicator label="Loading" size="small" textPosition="bottom" />}>
          <div className="main h-100">
            <SimsPageSearchContextProvider scopeKey={simsPageSearchScopeKey}>
              <Routes>
                <Route path="/company/:companyId/account/:accountId/*" element={<AccountRoutes />} />
                <Route path={`${companyUrl}sim`} element={<SimsPage />} />
                <Route path={`${companyUrl}sim/action/:type`} element={<SimsActionPage />} />
                <Route path={`${companyUrl}spendCap`} element={<SpendCapsPage />} />
                <Route path={`${companyUrl}sim/submission`} element={<SubmissionsPage />} />
                <Route path={`${companyUrl}sim/submission/:ticketNumber`} element={<SubmissionPage />} />
                <Route path={`${companyUrl}simorders`} element={<SimOrders />} />
                <Route path={`${companyUrl}simorders/create`} element={<SimOrderForm />} />
                <Route path={`${companyUrl}dashboard`} element={<GraphicalDashboard />} />
                <Route
                  path={`${companyUrl}portalManagement`}
                  element={
                    <AccountSelectionPage
                      title="Portal Administration"
                      targetPath="portalManagement/"
                      accountFilter={(account: AccountDetail) => account.portalCapable === true}
                      emptyMessage="No portal-capable accounts were found for this company."
                    />
                  }
                />
                <Route
                  path={`${companyUrl}porting-migration`}
                  element={<AccountSelectionPage title="Porting & Migration" targetPath="porting-migration" />}
                />
                <Route
                  path={`${companyUrl}alertManagerDefinitions`}
                  element={<AccountSelectionPage title="Alert Manager Definitions" targetPath="alertManagerDefinitions" />}
                />
              </Routes>
            </SimsPageSearchContextProvider>
          </div>
        </Suspense>
      ) : (
        <div className="csm-selector-menu-loading">
          <LoadingIndicator label="Loading" size="small" textPosition="bottom" />
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state: AppState): StateToProps => {
  return {
    company: state.filter.company,
    userSettings: state.userSettings
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchToProps, any> = (dispatch) => ({
  actions: bindActionCreators({ ensureSelectedCompany, ensureUserRoles }, dispatch)
});

export default withRouter(
  connect<StateToProps, DispatchToProps, OwnProps, AppState>(mapStateToProps, mapDispatchToProps)(CompanyRoutes)
);
